/**
 * "YOUR PLAN STOPS HERE" — one signal the app shell's banner listens to (CC-05 §2).
 *
 * Any API refusal (402 / 409) that carries `upgradeHint` sets it: Meter's capacity
 * limit (409 PLAN_LIMIT with `unitCode`), a spent allowance (402) or a locked feature
 * (`featureCode`). The hint sits on the problem body itself (RFC 9457) or under
 * `error.details`; both are read.
 */
export interface UpgradeHint {
  productCode: string;
  canTopUp: boolean;
  canUpgrade: boolean;
}

export interface LimitHit {
  hint: UpgradeHint;
  unitCode: string | null;
  featureCode: string | null;
}

type Listener = (hit: LimitHit | null) => void;
let current: LimitHit | null = null;
const listeners = new Set<Listener>();

export function getLimitHit(): LimitHit | null {
  return current;
}

export function setLimitHit(next: LimitHit | null): void {
  current = next;
  for (const l of listeners) l(next);
}

export function onLimitHit(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

type Bag = Record<string, unknown>;
const bag = (v: unknown): Bag | null => (v && typeof v === 'object' ? (v as Bag) : null);

/** The limit hit a refused response describes, or null when it is not one. */
export function limitHitFrom(status: number | undefined, body: unknown): LimitHit | null {
  if (status !== 402 && status !== 409) return null;
  const top = bag(body);
  const error = bag(top?.error);
  const where = [top, error, bag(error?.details), bag(top?.details)].find((p) => bag(p?.upgradeHint));
  if (!where) return null;
  const hint = bag(where.upgradeHint)!;
  return {
    hint: { productCode: String(hint.productCode ?? ''), canTopUp: hint.canTopUp === true, canUpgrade: hint.canUpgrade === true },
    unitCode: typeof where.unitCode === 'string' ? where.unitCode : null,
    featureCode: typeof where.featureCode === 'string' ? where.featureCode : null,
  };
}
